import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Fab,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { PoClosureRequestStatus } from "models/po-closure";
import StatusChip from "../general/StatusChip";
import { useAuth } from "src/contexts/AuthContext";
import { usePoClosureRequests } from "src/hooks/po-closure/usePoClosureRequests";
import {
  getNetSuiteLink,
  isAllowedPoClosureRequesterEmail,
} from "src/util/helpers";

const statusOptions = [
  "",
  PoClosureRequestStatus.PENDING,
  PoClosureRequestStatus.SUCCEEDED,
  PoClosureRequestStatus.FAILED,
  PoClosureRequestStatus.ALREADY_CLOSED,
];

const PoClosures: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(generateCurrentMonthValue());
  const [status, setStatus] = useState<PoClosureRequestStatus | "">("");
  const [lookup, setLookup] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [previousCursors, setPreviousCursors] = useState<(string | undefined)[]>([]);

  const { items, nextCursor, loading, error, fetchPoClosureRequests } = usePoClosureRequests();
  const months = useMemo(() => generateMonths(), []);
  const hasPreviousPage = previousCursors.length > 0;
  const hasNextPage = Boolean(nextCursor);
  const pageNumber = previousCursors.length + 1;
  const canUsePoClosureActions = isAllowedPoClosureRequesterEmail(user?.email);

  useEffect(() => {
    fetchPoClosureRequests({
      yearMonth: selectedMonth,
      status,
      lookup,
      requesterEmail,
      cursor: currentCursor,
      limit: 50,
    });
  }, [selectedMonth, status, lookup, requesterEmail, currentCursor]);

  const resetPagination = () => {
    setCurrentCursor(undefined);
    setPreviousCursors([]);
  };

  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(event.target.value);
    resetPagination();
  };

  const handleNextPage = () => {
    if (!nextCursor) {
      return;
    }
    setPreviousCursors((prev) => [...prev, currentCursor]);
    setCurrentCursor(nextCursor);
  };

  const handlePreviousPage = () => {
    setPreviousCursors((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const copy = [...prev];
      const previousCursor = copy.pop();
      setCurrentCursor(previousCursor);
      return copy;
    });
  };

  return (
    <Container sx={{ pt: 4, pb: 9 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
          variant="text"
        >
          Back
        </Button>
        <Typography variant="h4">PO Closure Audit Trail</Typography>
        <Box sx={{ width: 90 }} />
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Select value={selectedMonth} onChange={handleMonthChange} size="small">
          {months.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </Select>
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as PoClosureRequestStatus | "");
            resetPagination();
          }}
          size="small"
          displayEmpty
        >
          <MenuItem value="">All Statuses</MenuItem>
          {statusOptions
            .filter((option) => option)
            .map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
        </Select>
        <TextField
          size="small"
          label="PO Number or Vendor Prefix"
          value={lookup}
          onChange={(event) => {
            setLookup(event.target.value);
            resetPagination();
          }}
          sx={{ minWidth: 260 }}
        />
        <TextField
          size="small"
          label="Requester Email"
          value={requesterEmail}
          onChange={(event) => {
            setRequesterEmail(event.target.value);
            resetPagination();
          }}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      {loading ? (
        <Typography>Loading...</Typography>
      ) : items.length === 0 ? (
        <Typography>No PO closure records found.</Typography>
      ) : (
        <Box>
          {items.map((record) => (
            <Box
              key={record.requestId}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                py: 1.5,
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f7f7f7" },
              }}
              onClick={() => navigate(`/po-closures/${record.requestId}`)}
            >
              <Box sx={{ width: "18%" }}>
                <Typography variant="body2">Internal ID: {record.input.poInternalId}</Typography>
              </Box>
              <Box sx={{ width: "18%" }}>
                <Link
                  href={getNetSuiteLink("PurchaseOrder", record.input.poInternalId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  onClick={(event) => event.stopPropagation()}
                >
                  PO: {record.input.poNumber}
                </Link>
              </Box>
              <Box sx={{ width: "18%" }}>
                <Typography variant="body2">{record.input.vendorName}</Typography>
              </Box>
              <Box sx={{ width: "16%" }}>
                <Typography variant="body2">{record.requester.email}</Typography>
              </Box>
              <Box sx={{ width: "16%", pl: 2 }}>
                <StatusChip status={record.status} />
              </Box>
              <Box sx={{ width: "14%", textAlign: "right" }}>
                <Typography variant="body2">
                  {new Date(record.createdAt).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                  })}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="body2" color="text.secondary">
          Page {pageNumber}
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={handlePreviousPage}
            disabled={loading || !hasPreviousPage}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleNextPage}
            disabled={loading || !hasNextPage}
          >
            Next
          </Button>
        </Box>
      </Box>

      {canUsePoClosureActions ? (
        <Fab
          color="primary"
          size="large"
          variant="extended"
          sx={{ position: "fixed", bottom: 30, right: 24, zIndex: 1000 }}
          onClick={() => navigate("/po-closures/form")}
        >
          <AddIcon sx={{ mr: 1 }} />
          Close a PO
        </Fab>
      ) : null}
    </Container>
  );
};

const generateCurrentMonthValue = (): string => new Date().toISOString().substring(0, 7);

const generateMonths = (): { value: string; label: string }[] => {
  const startMonth = new Date(2024, 7, 2, 12, 0, 0);
  const currentMonth = new Date();
  const months = [];
  for (
    let dt = new Date(startMonth);
    dt <= currentMonth;
    dt.setMonth(dt.getMonth() + 1)
  ) {
    const value = dt.toISOString().substring(0, 7);
    const label = dt.toLocaleDateString("en-US", { year: "numeric", month: "long" });
    months.push({ value, label });
  }
  return months.reverse();
};

export default PoClosures;
